import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  IconButton,
  Typography,
  Alert,
  CircularProgress,
  Divider,
  Checkbox,
  FormControlLabel,
  Chip,
} from "@mui/material";
import { Close, Calculate, Add, Delete, Visibility } from "@mui/icons-material";
import userService from "../../services/userService";
import invoiceService from "../../services/invoiceService";
import InvoicePreviewDialog from "./InvoicePreviewDialog";
import { selectUsdToCiRate } from "../../redux/slices/systemSettingsSlice";
import { ciToUsd } from "../../utils/currency";

const blankForm = {
  customerEmail: "",
  shipmentTracking: "",
  freight: "",
  service: "",
  dueDate: "",
  notes: "",
};

const blankCalcInputs = {
  cbf: "",
  weightLb: "",
  merchandiseValue: "",
  deliverySelected: false,
};

// `shipment` (full object, with populated `items[]`) drives the per-item
// checklist + auto-calc. Without it, the dialog falls back to the old
// freeform freight/duty/service entry (used for manual, non-shipment
// invoices from the Invoices list page).
const InvoiceDialog = ({
  open,
  onClose,
  onSubmit,
  loading,
  defaultEmail = "",
  shipment,
}) => {
  const usdToCiRate = useSelector(selectUsdToCiRate);
  const [form, setForm] = useState(blankForm);
  const [customerOptions, setCustomerOptions] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const searchDebounceRef = useRef(null);

  const autoCalcMode = !!shipment;

  const [selectedItemIds, setSelectedItemIds] = useState(new Set());
  const [calcInputs, setCalcInputs] = useState(blankCalcInputs);
  const [calcLines, setCalcLines] = useState([]);
  // Ad-hoc charges the admin adds on top of whatever's already on the
  // invoice (auto-calculated or the three fixed manual fields) — anything
  // else, e.g. a storage fee or one-off adjustment. Works in both modes.
  const [extraLines, setExtraLines] = useState([]);
  const extraLineIdRef = useRef(0);
  const [freightBySailing, setFreightBySailing] = useState([]);
  // Customer's other still-unpaid invoices — carried forward as "Previous
  // pending from Invoice X" lines. Only resolvable once there's an actual
  // customer id (auto-calc mode always has one via shipment.customer; manual
  // mode only once an existing customer was picked from the Autocomplete,
  // not for a freeform new email).
  const [previousDues, setPreviousDues] = useState([]);
  const [includedDueIds, setIncludedDueIds] = useState(new Set());
  const [loadingDues, setLoadingDues] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewPayload, setPreviewPayload] = useState(null);
  const [calculating, setCalculating] = useState(false);
  const [calcError, setCalcError] = useState("");

  const itemRefs = useMemo(
    () =>
      [...selectedItemIds].map((itemId) => ({
        shipmentId: shipment?._id,
        itemId,
      })),
    [selectedItemIds, shipment],
  );

  const resolvedCustomerId = autoCalcMode ? shipment?.customer?._id : selectedCustomer?._id;

  useEffect(() => {
    if (!open || !resolvedCustomerId) {
      setPreviousDues([]);
      setIncludedDueIds(new Set());
      return;
    }
    setLoadingDues(true);
    invoiceService
      .getOutstanding({ customer: resolvedCustomerId })
      .then(({ data }) => {
        const dues = data.data.invoices || [];
        setPreviousDues(dues);
        setIncludedDueIds(new Set(dues.map((d) => d._id)));
      })
      .catch(() => setPreviousDues([]))
      .finally(() => setLoadingDues(false));
  }, [open, resolvedCustomerId]);

  const toggleDueIncluded = (id) => {
    setIncludedDueIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const previousDueLines = previousDues
    .filter((d) => includedDueIds.has(d._id))
    .map((d) => ({
      description: `Previous pending from Invoice ${d.invoiceNumber}`,
      unitPrice: d.balanceDue,
      quantity: 1,
    }));

  const runCalculate = async (overrides = {}) => {
    if (!itemRefs.length) {
      setCalcLines([]);
      setFreightBySailing([]);
      return;
    }
    setCalculating(true);
    setCalcError("");
    try {
      const { data } = await invoiceService.calculateItemsCharges({
        items: itemRefs,
        customerId: resolvedCustomerId,
        deliverySelected: calcInputs.deliverySelected,
        ...overrides,
      });
      const charges = data.data.charges;
      setCalcLines(charges.lines);
      setFreightBySailing(data.data.freightBySailing || []);
      if (overrides.resetTotals) {
        setCalcInputs((p) => ({
          ...p,
          cbf: charges.inputs.cbf,
          weightLb: charges.inputs.weightLb,
          merchandiseValue: charges.inputs.merchandiseValue,
        }));
      }
    } catch {
      setCalcError("Failed to calculate charges for the selected items");
    } finally {
      setCalculating(false);
    }
  };

  useEffect(() => {
    if (open) {
      setForm({ ...blankForm, customerEmail: defaultEmail });
      setSelectedCustomer(null);
      setCustomerOptions([]);
      setLoadingCustomers(false);
      setCalcError("");
      setCalcLines([]);
      setExtraLines([]);
      setPreviousDues([]);
      setIncludedDueIds(new Set());
      setFreightBySailing([]);

      if (shipment) {
        const defaultSelected = new Set(
          (shipment.items || []).filter((i) => !i.invoiced).map((i) => i._id),
        );
        setSelectedItemIds(defaultSelected);
        setCalcInputs({ ...blankCalcInputs, deliverySelected: !!shipment.deliverySelected });
      } else {
        setSelectedItemIds(new Set());
        setCalcInputs(blankCalcInputs);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, defaultEmail, shipment]);

  // Re-fetch computed totals fresh whenever the item selection changes —
  // any manual override in the fields below is intentionally discarded so
  // the numbers always reflect exactly what's checked.
  useEffect(() => {
    if (open && autoCalcMode) {
      runCalculate({ resetTotals: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItemIds, open]);

  useEffect(() => {
    let active = true;
    const search = form.customerEmail?.trim();

    if (!open || autoCalcMode || !search || search.length < 2) {
      setCustomerOptions([]);
      setLoadingCustomers(false);
      return () => {
        active = false;
        clearTimeout(searchDebounceRef.current);
      };
    }

    const fetchCustomers = async () => {
      setLoadingCustomers(true);
      try {
        const { data } = await userService.getAll({
          role: "customer",
          search,
          limit: 10,
        });
        if (active) {
          setCustomerOptions(data.data || []);
        }
      } catch {
        if (active) {
          setCustomerOptions([]);
        }
      } finally {
        if (active) {
          setLoadingCustomers(false);
        }
      }
    };

    clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      fetchCustomers();
    }, 1000);

    return () => {
      active = false;
      clearTimeout(searchDebounceRef.current);
    };
  }, [form.customerEmail, open, autoCalcMode]);

  const setField = (field) => (event) => {
    const value = event.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
    if (field === "customerEmail") {
      setSelectedCustomer(null);
    }
  };

  const handleCustomerChange = (_, newValue) => {
    if (typeof newValue === "string") {
      setSelectedCustomer(null);
      setForm((prev) => ({ ...prev, customerEmail: newValue }));
      return;
    }

    if (newValue && newValue.email) {
      setSelectedCustomer(newValue);
      setForm((prev) => ({ ...prev, customerEmail: newValue.email }));
      return;
    }

    setSelectedCustomer(null);
  };

  const toggleItem = (itemId) => {
    setSelectedItemIds((prev) => {
      const next = new Set(prev);
      next.has(itemId) ? next.delete(itemId) : next.add(itemId);
      return next;
    });
  };

  const setCalcField = (field) => (event) =>
    setCalcInputs((p) => ({ ...p, [field]: event.target.value }));

  const setLineAmount = (index) => (event) => {
    const value = event.target.value;
    setCalcLines((prev) =>
      prev.map((l, i) => (i === index ? { ...l, amount: value } : l)),
    );
  };

  const addExtraLine = () => {
    extraLineIdRef.current += 1;
    setExtraLines((prev) => [...prev, { id: extraLineIdRef.current, description: "", amount: "" }]);
  };
  const removeExtraLine = (id) => setExtraLines((prev) => prev.filter((l) => l.id !== id));
  const setExtraLineField = (id, field) => (event) => {
    const value = event.target.value;
    setExtraLines((prev) => prev.map((l) => (l.id === id ? { ...l, [field]: value } : l)));
  };
  const extraLinesTotal = extraLines.reduce((s, l) => s + (parseFloat(l.amount) || 0), 0);

  const previousDuesTotal = previousDueLines.reduce((s, l) => s + l.unitPrice, 0);

  const total =
    (autoCalcMode
      ? calcLines.reduce((s, l) => s + (parseFloat(l.amount) || 0), 0)
      : (parseFloat(form.freight) || 0) + (parseFloat(form.service) || 0)) +
    extraLinesTotal +
    previousDuesTotal;

  const buildInvoicePayload = () => {
    const baseLines = autoCalcMode
      ? calcLines
          .filter((l) => parseFloat(l.amount))
          .map((l) => ({
            description: l.description,
            unitPrice: parseFloat(l.amount),
            quantity: 1,
          }))
      : [
          form.freight && {
            description: "Freight & Handling",
            unitPrice: parseFloat(form.freight),
            quantity: 1,
          },
          form.service && {
            description: "Service Fee",
            unitPrice: parseFloat(form.service),
            quantity: 1,
          },
        ].filter(Boolean);

    const lines = [
      ...baseLines,
      ...extraLines
        .filter((l) => l.description.trim() && parseFloat(l.amount))
        .map((l) => ({ description: l.description.trim(), unitPrice: parseFloat(l.amount), quantity: 1 })),
      ...previousDueLines,
    ];

    return {
      customerEmail: autoCalcMode ? shipment.customer?.email : form.customerEmail,
      customer: autoCalcMode ? shipment.customer?._id : selectedCustomer?._id,
      shipment: autoCalcMode ? shipment._id : undefined,
      items: autoCalcMode ? itemRefs : undefined,
      lines,
      dueDate: form.dueDate,
      notes: form.notes,
    };
  };

  const handleOpenPreview = () => {
    setPreviewPayload(buildInvoicePayload());
    setPreviewOpen(true);
  };

  const handleConfirmSend = () => onSubmit(buildInvoicePayload());

  const items = shipment?.items || [];
  const canSubmit = autoCalcMode
    ? selectedItemIds.size > 0 && form.dueDate && total > 0 && !loading
    : !!form.customerEmail && !!form.dueDate && total > 0 && !loading;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography fontWeight={700}>Generate Invoice</Typography>
        <IconButton size="small" onClick={onClose}>
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ pt: 2.5 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {autoCalcMode ? (
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              Invoicing <strong>{shipment.customer?.firstName} {shipment.customer?.lastName}</strong> for shipment{" "}
              <strong>{shipment.trackingNumber}</strong>. Select which items to include.
            </Alert>
          ) : (
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              Select an existing customer or enter any other email address.
            </Alert>
          )}

          {!autoCalcMode && (
            <Autocomplete
              freeSolo
              options={customerOptions}
              getOptionLabel={(option) =>
                typeof option === "string" ? option : option.email
              }
              value={selectedCustomer || form.customerEmail}
              loading={loadingCustomers}
              onChange={handleCustomerChange}
              inputValue={form.customerEmail}
              onInputChange={(_, newInputValue) =>
                setField("customerEmail")({ target: { value: newInputValue } })
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Customer Email"
                  type="email"
                  required
                  helperText="Search existing customers or type a new email."
                  slotProps={{
                    ...params.slotProps,
                    input: {
                      ...params.slotProps?.input,
                      endAdornment: (
                        <>
                          {loadingCustomers && (
                            <CircularProgress color="inherit" size={20} />
                          )}

                          {params.slotProps?.input?.endAdornment}
                        </>
                      ),
                    },
                  }}
                />
              )}
            />
          )}

          <TextField
            label="Due Date"
            type="date"
            value={form.dueDate}
            onChange={setField("dueDate")}
            slotProps={{ inputLabel: { shrink: true } }}
            required
            sx={{
              "& .MuiInputLabel-root": {
                backgroundColor: "background.paper",
                px: 0.5,
                transform: "translate(14px, -9px) scale(0.75)",
              },
            }}
          />

          {autoCalcMode ? (
            <>
              <Divider>
                <Typography variant="caption" color="text.secondary">
                  Items to Invoice
                </Typography>
              </Divider>
              {items.length === 0 && (
                <Alert severity="warning" sx={{ borderRadius: 2 }}>
                  This shipment has no items.
                </Alert>
              )}
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, maxHeight: 220, overflowY: "auto" }}>
                {items.map((item) => (
                  <Box
                    key={item._id}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      py: 0.5,
                      opacity: item.invoiced ? 0.5 : 1,
                    }}
                  >
                    <Checkbox
                      size="small"
                      checked={selectedItemIds.has(item._id)}
                      onChange={() => toggleItem(item._id)}
                      disabled={item.invoiced}
                    />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" noWrap>
                        {item.description}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        D/R {item.dockReceiptNumber || "—"} ·{" "}
                        {item.weight != null ? `${item.weight} lb` : "no weight"} ·{" "}
                        {item.cbf != null ? `${item.cbf} cbf` : "no cbf"} · $
                        {(item.totalValue || 0).toFixed(2)}
                      </Typography>
                    </Box>
                    {item.invoiced && <Chip size="small" label="Invoiced" />}
                  </Box>
                ))}
              </Box>

              <Divider>
                <Typography variant="caption" color="text.secondary">
                  Auto-Calculated Charges
                </Typography>
              </Divider>
              {calcError && (
                <Alert severity="warning" sx={{ borderRadius: 2 }}>
                  {calcError}
                </Alert>
              )}
              <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 2 }}>
                <TextField
                  label="Total CBF"
                  type="number"
                  value={calcInputs.cbf}
                  onChange={setCalcField("cbf")}
                  helperText="Summed from selected items"
                />
                <TextField
                  label="Weight (lbs)"
                  type="number"
                  value={calcInputs.weightLb}
                  onChange={setCalcField("weightLb")}
                  helperText="Summed from selected items"
                />
                <TextField
                  label="Merchandise Value"
                  type="number"
                  value={calcInputs.merchandiseValue}
                  onChange={setCalcField("merchandiseValue")}
                  helperText="Summed from selected items"
                  slotProps={{
                    input: {
                      startAdornment: (
                        <Typography sx={{ mr: 0.5, color: "text.secondary", fontSize: 13 }}>$</Typography>
                      ),
                    },
                  }}
                />
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!!calcInputs.deliverySelected}
                      onChange={(e) =>
                        setCalcInputs((p) => ({ ...p, deliverySelected: e.target.checked }))
                      }
                    />
                  }
                  label="Delivery"
                />
              </Box>
              <Button
                variant="outlined"
                size="small"
                startIcon={calculating ? <CircularProgress size={14} /> : <Calculate />}
                onClick={() => runCalculate()}
                disabled={calculating || selectedItemIds.size === 0}
                sx={{ alignSelf: "flex-start" }}
              >
                Recalculate
              </Button>

              {calcLines.length > 0 && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {calcLines.map((line, i) => (
                    <Box key={line.key} sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      <Typography variant="body2" sx={{ flex: 1 }}>
                        {line.description}
                      </Typography>
                      <TextField
                        size="small"
                        type="number"
                        value={line.amount}
                        onChange={setLineAmount(i)}
                        sx={{ width: 120 }}
                        slotProps={{
                          input: {
                            startAdornment: (
                              <Typography sx={{ mr: 0.5, color: "text.secondary", fontSize: 13 }}>$</Typography>
                            ),
                          },
                        }}
                      />
                    </Box>
                  ))}
                </Box>
              )}

              {freightBySailing.length > 0 && (
                <Box
                  sx={{
                    bgcolor: "action.hover",
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                    p: 1.5,
                  }}
                >
                  <Typography variant="overline" color="text.secondary" display="block" mb={0.5}>
                    Allocated Freight (from Manifest Cover Sheet)
                  </Typography>
                  {freightBySailing.map((s) => (
                    <Box
                      key={s.sailingId}
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        py: 0.5,
                        "&:not(:last-of-type)": { borderBottom: "1px solid", borderColor: "divider" },
                      }}
                    >
                      <Box>
                        <Typography variant="body2" fontWeight={700}>
                          Sailing {s.sailingNumber}
                        </Typography>
                        {s.hblNumber && (
                          <Typography variant="caption" color="text.secondary">
                            Manifest / HBOL {s.hblNumber}
                          </Typography>
                        )}
                      </Box>
                      <Typography variant="body2" color="primary.main" fontWeight={600}>
                        CI${s.customerAllocatedFreight.toFixed(2)}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </>
          ) : (
            <>
              <Divider>
                <Typography variant="caption" color="text.secondary">
                  Charge Items
                </Typography>
              </Divider>
              <Box
                sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}
              >
                <TextField
                  label="Freight & Handling"
                  type="number"
                  value={form.freight}
                  onChange={setField("freight")}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <Typography
                          sx={{ mr: 0.5, color: "text.secondary", fontSize: 13 }}
                        >
                          $
                        </Typography>
                      ),
                    },
                  }}
                />
                <TextField
                  label="Service Fee"
                  type="number"
                  value={form.service}
                  onChange={setField("service")}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <Typography
                          sx={{ mr: 0.5, color: "text.secondary", fontSize: 13 }}
                        >
                          $
                        </Typography>
                      ),
                    },
                  }}
                />
              </Box>
            </>
          )}

          <Divider>
            <Typography variant="caption" color="text.secondary">
              Extra Charges
            </Typography>
          </Divider>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {extraLines.map((line) => (
              <Box key={line.id} sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <TextField
                  size="small"
                  placeholder="Description (e.g. Storage Fee)"
                  value={line.description}
                  onChange={setExtraLineField(line.id, "description")}
                  sx={{ flex: 1 }}
                />
                <TextField
                  size="small"
                  type="number"
                  placeholder="Amount"
                  value={line.amount}
                  onChange={setExtraLineField(line.id, "amount")}
                  sx={{ width: 120 }}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <Typography sx={{ mr: 0.5, color: "text.secondary", fontSize: 13 }}>$</Typography>
                      ),
                    },
                  }}
                />
                <Button
                  size="small"
                  color="error"
                  onClick={() => removeExtraLine(line.id)}
                  sx={{ minWidth: 0, p: 0.75 }}
                >
                  <Delete fontSize="small" />
                </Button>
              </Box>
            ))}
            <Button size="small" startIcon={<Add />} onClick={addExtraLine} sx={{ alignSelf: "flex-start" }}>
              Add Charge
            </Button>
          </Box>

          {loadingDues && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
              <CircularProgress size={18} />
            </Box>
          )}
          {previousDues.length > 0 && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Typography variant="overline" color="text.secondary">
                Previous Pending Dues
              </Typography>
              {previousDues.map((d) => (
                <Box key={d._id} sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Checkbox
                    size="small"
                    checked={includedDueIds.has(d._id)}
                    onChange={() => toggleDueIncluded(d._id)}
                    sx={{ p: 0.5 }}
                  />
                  <Typography variant="body2" sx={{ flex: 1 }}>
                    Previous pending from Invoice {d.invoiceNumber}
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    ${d.balanceDue.toFixed(2)}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}

          {total > 0 && (
            <Box
              sx={{
                bgcolor: "action.hover",
                borderRadius: 2,
                p: 2,
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <Box>
                <Typography fontWeight={700}>Total Invoice Amount</Typography>
                <Typography variant="caption" color="text.secondary">
                  ≈ ${ciToUsd(total, usdToCiRate).toFixed(2)}
                </Typography>
              </Box>
              <Typography fontWeight={700} fontSize={17} color="primary.main">
                CI${total.toFixed(2)}
              </Typography>
            </Box>
          )}
          <TextField
            label="Notes (optional)"
            multiline
            rows={2}
            value={form.notes}
            onChange={setField("notes")}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} variant="outlined">
          Cancel
        </Button>
        <Button
          onClick={handleOpenPreview}
          variant="contained"
          startIcon={<Visibility />}
          disabled={!canSubmit}
        >
          View Invoice
        </Button>
      </DialogActions>

      <InvoicePreviewDialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        onEdit={() => setPreviewOpen(false)}
        payload={previewPayload}
        onConfirm={handleConfirmSend}
        confirming={loading}
      />
    </Dialog>
  );
};

export default InvoiceDialog;
